import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("E-posta ve şifre gerekli");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Giriş başarılı");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F6F8FB]">
      <Toaster position="top-right" richColors />

      {/* Left: Brand panel with building background */}
      <div
        className="lg:w-2/5 text-white p-8 lg:p-12 relative overflow-hidden flex flex-col bg-cover bg-center"
        style={{ backgroundImage: 'url("/azdgiris.jpg")' }}
      >
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#08285A]/85 via-[#08285A]/70 to-[#08285A]/90 pointer-events-none" />
        <div className="relative z-10 flex-1 flex flex-col">
          <div className="flex items-center mb-12">
            <div className="bg-white rounded-xl px-6 py-4 shadow-2xl">
              <img src="/azd-logo.png" alt="AZD Ambalaj Sanayi ve Ticaret Limited Şirketi"
                   className="h-16 w-auto" />
            </div>
          </div>

          <div className="flex-1 flex flex-col justify-center max-w-md">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#FFE600] font-bold mb-3">
              ŞİRKET İÇİ YÖNETİM SİSTEMİ
            </div>
            <h1 className="text-3xl lg:text-4xl font-extrabold leading-tight mb-4">
              Stoktan satışa,<br />
              <span className="text-[#FFE600]">cariden raporlamaya</span> —<br />
              tek panelden.
            </h1>
            <p className="text-white/70 text-sm leading-relaxed">
              AZD Flow; ürün, stok, firma, teklif, sipariş, ödeme ve raporlama
              süreçlerinizi tek bir kurumsal sistemde birleştirir.
            </p>
          </div>

          <div className="relative z-10 text-[11px] text-white/40 mt-8">
            © {new Date().getFullYear()} AZD Ambalaj San. ve Tic. Ltd. Şti.
          </div>
        </div>
      </div>

      {/* Right: Login */}
      <div className="lg:w-3/5 p-6 lg:p-12 flex flex-col">
        <div className="max-w-md mx-auto w-full flex-1 flex flex-col justify-center">
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#0B3676] font-bold mb-2">
              Giriş Yap
            </div>
            <h2 className="text-2xl lg:text-3xl font-extrabold text-[#08285A]">
              AZD Flow&apos;a Hoş Geldiniz
            </h2>
            <p className="text-[#6B7280] text-sm mt-2">
              Devam etmek için e-posta ve şifreniz ile oturum açın.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="azd-card p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#111827] mb-1.5">
                  E-Posta
                </label>
                <Input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@azd.com.tr"
                  autoComplete="email"
                  className="h-11"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#111827] mb-1.5">
                  Şifre
                </label>
                <div className="relative">
                  <Input
                    data-testid="login-password"
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    data-testid="toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0B3676]"
                  >
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                data-testid="login-submit"
                disabled={loading}
                className="w-full h-11 bg-[#0B3676] hover:bg-[#08285A] text-white font-semibold"
              >
                {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
              </Button>
            </div>
          </form>
        </div>

        <footer
          className="pt-6 pb-2 text-center text-[11px] text-[#6B7280]"
          data-testid="login-footer"
        >
          Bu şirket yönetim sistemi{" "}
          <a
            href="https://www.dijitalroket.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-[#0B3676] hover:text-[#08285A] hover:underline transition-colors"
          >
            Dijital Roket
          </a>{" "}
          tarafından geliştirilmiştir.
        </footer>
      </div>
    </div>
  );
}
